import { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { AnimatePresence } from 'framer-motion';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
  type CellContext,
  flexRender,
} from '@tanstack/react-table';
import {
  Dropdown,
  Modal,
  Button,
  Input,
  MenuProps,
  Skeleton,
  Badge,
  Select,
  Tooltip,
  Checkbox,
  Tabs,
  DatePicker,
  Upload,
  Pagination,
} from 'antd';
import {
  SearchOutlined,
  SwapOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  EllipsisVerticalIcon,
  DownloadIcon,
  ArrowLeft,
  RotateCcw,
  RefreshCw,
  FileSpreadsheet,
  FileUp,
  X,
  Send,
  FileText,
  MessageSquare,
  Inbox,
} from 'lucide-react';
import { CardWizard, SideSheet } from '../../layouts';
import { ConfirmationDialog, NotFound } from '../../components';
import GlobalLoader from '../../components/GlobalLoader';
import CommonTravelTable from '../../components/common/components/CommonTravelTable';
import {
  useGetPaymentReminderAgingSummaryQuery,
  useGetCustomerInvoicesQuery,
  useGetAllInvoicesQuery,
  useGetCustomersForDropdownQuery,
  useSendPaymentReminderEmailMutation,
  useBulkSendPaymentReminderEmailMutation,
  useGetMailLogsQuery,
  useCreateCustomerRemarkMutation,
  useGetCustomerRemarksQuery,
  useUpdateCustomerRemarkMutation,
  useDeleteCustomerRemarkMutation,
  useLazyGetFilePreviewQuery,
  useLazyExportAgingSummaryQuery,
  useUpdatePaymentReminderFileMutation,
  useLazyDownloadTemplateQuery,
  useGetBucketInvoiceDetailsQuery,
} from '../../store';
import { useNotification } from '../../contexts';
import {
  extractErrorMessageFromAPI,
  formatDayJsDate,
  formatAmountNoSymbol,
  downloadFileFromBlob,
  getCommonColumnPinningStyles,
  getCommonColumnPinningStylesForHeader,
} from '../../utils';
import { useForm, Controller } from 'react-hook-form';
import {
  PaymentReminderMailLogSortBy,
  PaymentReminderRemarksSortBy,
  PaymentReminderInvoiceSortBy,
} from './constants/paymentReminder.enum';
import BucketInvoiceDetailsModal from './components/BucketInvoiceDetailsModal';
import { AgingSummaryDashboard } from './components/AgingSummaryDashboard';
import { BucketColorLegend } from './components/BucketColorLegend';
import { getBucketColorConfig } from './utils/bucketHelpers';

/* ================= TYPES ================= */

interface RecentRemark {
  id: number;
  remarks: string;
  createdAt: string;
}

interface CustomerRow {
  customerId: number;
  customerNumber: string;
  customerName: string;
  customerEmail: string;
  ccEmails: string[] | null;
  aging: Record<string, number>;
  totalInvoiceAmount: number;
  lastMailSentAt: string | null;
  recentRemark: RecentRemark | null;
}

interface Invoice {
  id: number;
  fileId: number;
  customerId?: number;
  sapDocumentNumber: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  invoiceAmount: string | number;
  invoiceAmountInr?: string | number | null;
  currency: string | null;
  createdAt: string;
  customerNumber?: string;
  customer: {
    id?: number;
    customerNumber?: string;
    customerName: string;
    customerEmail: string;
  };
  status?: string;
  statusLabel?: string;
  statusColor?: string;
  agingDays?: number;
}

interface Remark {
  id: number;
  customerId: number;
  remarks: string;
  createdAt: string;
  createdById: number | null;
  updatedById: number | null;
  deletedById: number | null;
  createdByUser: any | null;
  updatedByUser: any | null;
}

interface MailLog {
  id: number;
  fileId: number;
  customerId: number;
  customerNumber: string;
  customerEmail: string;
  ccEmails: string[];
  subject: string;
  status: string;
  sentAt: string | null;
  sentVia: string | null;
  sentBy: {
    id: number;
    name: string;
    email: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

interface RemarkFormData {
  remarks: string;
}

const columnHelper = createColumnHelper<CustomerRow>();

/* ================= COMPONENT ================= */

export default function PaymentReminderCustomerBuckets() {
  const { fileId } = useParams<{ fileId: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const fileIdNum = fileId ? parseInt(fileId, 10) : 0;

  if (!fileIdNum) {
    return <NotFound />;
  }

  // States
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
    null,
  );
  const [isInvoicesSideSheetOpen, setIsInvoicesSideSheetOpen] = useState(false);
  const [isMailLogsSideSheetOpen, setIsMailLogsSideSheetOpen] = useState(false);
  const [isRemarksModalOpen, setIsRemarksModalOpen] = useState(false);
  const [editingRemark, setEditingRemark] = useState<Remark | null>(null);
  const [remarkToDelete, setRemarkToDelete] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'customerBucket' | 'invoices'>(
    'customerBucket',
  );
  const [customerSearch, setCustomerSearch] = useState<string>('');
  const [invoiceSearch, setInvoiceSearch] = useState<string>('');
  const [customerSortOrder, setCustomerSortOrder] = useState<string>('asc');
  const [invoicePage, setInvoicePage] = useState<number>(1);
  const [invoiceLimit, setInvoiceLimit] = useState<number>(25);
  const [invoiceSortBy, setInvoiceSortBy] = useState<string>(
    PaymentReminderInvoiceSortBy.LATEST_RECENT_DATE,
  );
  const [invoiceCustomerId, setInvoiceCustomerId] = useState<
    number | undefined
  >(undefined);
  const [invoiceDateFrom, setInvoiceDateFrom] = useState<dayjs.Dayjs | null>(
    null,
  );
  const [invoiceDateTo, setInvoiceDateTo] = useState<dayjs.Dayjs | null>(null);
  const [dueDateFrom, setDueDateFrom] = useState<dayjs.Dayjs | null>(null);
  const [dueDateTo, setDueDateTo] = useState<dayjs.Dayjs | null>(null);
  const [mailLogSortBy, setMailLogSortBy] = useState<string | undefined>(
    undefined,
  );
  const [remarksSortBy, setRemarksSortBy] = useState<string | undefined>(
    PaymentReminderRemarksSortBy.CREATED_DATE_DESC,
  );
  const [selectedCustomers, setSelectedCustomers] = useState<number[]>([]);
  const [customerPage, setCustomerPage] = useState<number>(1);
  const [customerPageSize, setCustomerPageSize] = useState<number>(25);
  const [isManualRefresh, setIsManualRefresh] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [updateFileList, setUpdateFileList] = useState<any[]>([]);
  const [bucketInvoiceDetails, setBucketInvoiceDetails] = useState<{
    customerId: number;
    bucketCode: string;
  } | null>(null);
  const [bucketInvoicePage, setBucketInvoicePage] = useState<number>(1);
  const [bucketInvoicePageSize, setBucketInvoicePageSize] =
    useState<number>(25);
  const [isBucketInvoiceModalOpen, setIsBucketInvoiceModalOpen] =
    useState<boolean>(false);
  const [lastBucketInvoiceRequest, setLastBucketInvoiceRequest] =
    useState<string>('');
  const [shouldClearData, setShouldClearData] = useState<boolean>(false);

  // Customer Invoices side sheet pagination
  const [customerInvoicePage, setCustomerInvoicePage] = useState<number>(1);
  const [customerInvoicePageSize, setCustomerInvoicePageSize] =
    useState<number>(25);

  // Bucket Invoice Details Query - Only call when modal is actually open
  const {
    data: bucketInvoiceData,
    isLoading: isLoadingBucketInvoices,
    isFetching: isFetchingBucketInvoices,
    error: bucketInvoiceError,
  } = useGetBucketInvoiceDetailsQuery(
    {
      fileId: fileIdNum,
      customerId: bucketInvoiceDetails?.customerId || 0,
      bucketCode: bucketInvoiceDetails?.bucketCode || '',
      page: bucketInvoicePage,
      limit: bucketInvoicePageSize,
    },
    {
      skip: !isBucketInvoiceModalOpen || !bucketInvoiceDetails,
      // Force refetch when modal opens to get fresh data
      refetchOnMountOrArgChange: true,
    },
  );

  // Track current request to avoid showing stale data (memoized to prevent re-renders)
  const currentRequestKey = useMemo(() => {
    return bucketInvoiceDetails
      ? `${bucketInvoiceDetails.customerId}-${bucketInvoiceDetails.bucketCode}-${bucketInvoicePage}`
      : '';
  }, [
    bucketInvoiceDetails?.customerId,
    bucketInvoiceDetails?.bucketCode,
    bucketInvoicePage,
  ]);

  // Check if data matches current request (memoized to prevent infinite loops)
  const dataMatchesRequest = useMemo(() => {
    if (!bucketInvoiceData) return false;
    if (lastBucketInvoiceRequest === '') return true; // No request key set yet, assume match
    if (currentRequestKey === '') return false; // No current request, can't match
    return currentRequestKey === lastBucketInvoiceRequest;
  }, [bucketInvoiceData, lastBucketInvoiceRequest, currentRequestKey]);

  // Clear data immediately when bucketInvoiceDetails changes (before modal opens)
  useEffect(() => {
    if (bucketInvoiceDetails) {
      const requestKey = `${bucketInvoiceDetails.customerId}-${bucketInvoiceDetails.bucketCode}-${bucketInvoicePage}`;
      // Only update if request key actually changed to prevent unnecessary re-renders
      if (lastBucketInvoiceRequest !== requestKey) {
        setShouldClearData(true);
        setLastBucketInvoiceRequest(requestKey);
      }
    } else {
      // Reset when bucketInvoiceDetails is cleared
      setLastBucketInvoiceRequest('');
      setShouldClearData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bucketInvoiceDetails?.customerId,
    bucketInvoiceDetails?.bucketCode,
    bucketInvoicePage,
  ]);

  // Clear the flag when we have matching fresh data (only when data is actually loaded)
  useEffect(() => {
    if (
      shouldClearData &&
      dataMatchesRequest &&
      !isFetchingBucketInvoices &&
      !isLoadingBucketInvoices &&
      bucketInvoiceData
    ) {
      setShouldClearData(false);
    }
  }, [
    shouldClearData,
    dataMatchesRequest,
    isFetchingBucketInvoices,
    isLoadingBucketInvoices,
    bucketInvoiceData,
  ]);

  // Show loader if: loading, fetching, or clearing data for new request
  const shouldShowLoader = useMemo(() => {
    return (
      isLoadingBucketInvoices ||
      isFetchingBucketInvoices ||
      shouldClearData ||
      (lastBucketInvoiceRequest !== '' &&
        currentRequestKey !== '' &&
        currentRequestKey !== lastBucketInvoiceRequest)
    );
  }, [
    isLoadingBucketInvoices,
    isFetchingBucketInvoices,
    shouldClearData,
    lastBucketInvoiceRequest,
    currentRequestKey,
  ]);

  // Show data if: we have data, it matches current request, and we're not clearing
  const shouldShowData = useMemo(() => {
    if (!bucketInvoiceData) return false;
    if (shouldClearData) return false;
    return dataMatchesRequest;
  }, [bucketInvoiceData, shouldClearData, dataMatchesRequest]);

  // API Queries
  const {
    data: agingSummaryData,
    isLoading: isLoadingAgingSummary,
    isFetching: isFetchingAgingSummary,
    refetch: refetchAgingSummary,
  } = useGetPaymentReminderAgingSummaryQuery({
    fileId: fileIdNum,
    search: customerSearch || undefined,
    sortOrder: customerSortOrder,
    page: customerPage,
    limit: customerPageSize,
  });

  const { data: invoicesData, isLoading: isLoadingInvoices } =
    useGetCustomerInvoicesQuery(
      {
        fileId: fileIdNum,
        customerId: selectedCustomerId || 0,
        page: customerInvoicePage,
        pageSize: customerInvoicePageSize,
      },
      {
        skip: !selectedCustomerId || !isInvoicesSideSheetOpen,
        refetchOnMountOrArgChange: true,
      },
    );

  // Fetch all invoices data on mount (not skipped by viewMode) for instant tab switching
  // RTK Query will cache the data, making tab switches feel instant
  const {
    data: allInvoicesResponse,
    isLoading: isLoadingAllInvoices,
    isFetching: isFetchingAllInvoices,
    refetch: refetchAllInvoices,
  } = useGetAllInvoicesQuery(
    {
      fileId: fileIdNum,
      page: invoicePage,
      limit: invoiceLimit,
      search: invoiceSearch || undefined,
      sortBy: invoiceSortBy,
      customerId: invoiceCustomerId,
      invoiceDateFrom: invoiceDateFrom
        ? invoiceDateFrom.toISOString()
        : undefined,
      invoiceDateTo: invoiceDateTo ? invoiceDateTo.toISOString() : undefined,
      dueDateFrom: dueDateFrom ? dueDateFrom.toISOString() : undefined,
      dueDateTo: dueDateTo ? dueDateTo.toISOString() : undefined,
    },
    {
      skip: !fileIdNum,
    },
  );

  const { data: customersDropdownData, isLoading: isLoadingCustomersDropdown } =
    useGetCustomersForDropdownQuery(
      { fileId: fileIdNum },
      { skip: !fileIdNum },
    );

  const handleRefresh = async () => {
    setIsManualRefresh(true);
    try {
      if (viewMode === 'customerBucket') {
        await refetchAgingSummary();
      } else {
        await refetchAllInvoices();
      }
    } finally {
      setIsManualRefresh(false);
    }
  };

  const {
    data: mailLogsResponse,
    refetch: refetchMailLogs,
    isFetching: isMailLogsLoading,
  } = useGetMailLogsQuery(
    {
      fileId: Number(fileId),
      customerId: selectedCustomerId,
      sortBy: mailLogSortBy,
    },
    {
      skip: !selectedCustomerId || !isMailLogsSideSheetOpen,
    },
  );

  const {
    data: remarksData,
    isLoading: isLoadingRemarks,
    refetch: refetchRemarks,
  } = useGetCustomerRemarksQuery(
    {
      fileId: fileIdNum,
      customerId: selectedCustomerId || 0,
      sortBy: remarksSortBy,
    },
    {
      skip: !isRemarksModalOpen || !fileIdNum || !selectedCustomerId,
    },
  );

  // Mutations
  const [sendEmail, { isLoading: isSendingEmail }] =
    useSendPaymentReminderEmailMutation();
  const [bulkSendEmail, { isLoading: isBulkSendingEmail }] =
    useBulkSendPaymentReminderEmailMutation();
  const [createRemark, { isLoading: isCreatingRemark }] =
    useCreateCustomerRemarkMutation();
  const [updateRemark, { isLoading: isUpdatingRemark }] =
    useUpdateCustomerRemarkMutation();
  const [deleteRemark, { isLoading: isDeletingRemark }] =
    useDeleteCustomerRemarkMutation();
  const [downloadFile, { isLoading: isDownloadingFile }] =
    useLazyGetFilePreviewQuery();
  const [exportAgingSummary, { isLoading: isExportingAgingSummary }] =
    useLazyExportAgingSummaryQuery();
  const [updateFile, { isLoading: isUpdatingFile }] =
    useUpdatePaymentReminderFileMutation();
  const [downloadTemplate, { isLoading: isDownloadingTemplate }] =
    useLazyDownloadTemplateQuery();

  // Form
  const remarkForm = useForm<RemarkFormData>({
    defaultValues: {
      remarks: '',
    },
  });

  // Data
  const buckets = useMemo(() => {
    if (!agingSummaryData?.buckets) return [];
    return [...agingSummaryData.buckets].sort(
      (a, b) => a.sortOrder - b.sortOrder,
    );
  }, [agingSummaryData]);

  const customers: CustomerRow[] = useMemo(
    () =>
      (agingSummaryData?.customers || []).map((customer: any) => ({
        ...customer,
        totalInvoiceAmount:
          customer.totalInvoiceAmountInr ??
          customer.totalInvoiceAmount ??
          customer.totalGrossAmount ??
          0,
        recentRemark: customer.recentRemark || null,
      })),
    [agingSummaryData?.customers],
  );
  const fileInfo = agingSummaryData?.file;
  const customersTotal = agingSummaryData?.meta?.total || 0;
  const invoices = invoicesData?.data?.list || [];
  const invoicesTotal = invoicesData?.data?.count || 0;
  const allInvoices: Invoice[] = Array.isArray(allInvoicesResponse?.data)
    ? allInvoicesResponse.data.map((inv: any) => ({
        ...inv,
        customerId: inv.customerId,
        customerNumber:
          inv.customerNumber || inv.customer?.customerNumber || '',
        invoiceAmount: inv.invoiceAmount ?? inv.grossAmount,
        invoiceAmountInr: inv.invoiceAmountInr ?? null,
        currency: inv.currency ?? null,
      }))
    : [];
  const allInvoicesTotal = allInvoicesResponse?.meta?.total || 0;
  const mailLogs: MailLog[] = Array.isArray(mailLogsResponse?.data)
    ? mailLogsResponse.data.map((log: any) => ({
        ...log,
        sentBy: log.sentBy || null,
      }))
    : [];
  const remarks: Remark[] = Array.isArray(remarksData?.data)
    ? remarksData.data
    : [];

  useEffect(() => {
    if (isRemarksModalOpen && selectedCustomerId && fileIdNum) {
      refetchRemarks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRemarksModalOpen, selectedCustomerId, fileIdNum]);

  /* ================= HANDLERS ================= */

  const handleDownloadFile = async () => {
    if (!fileIdNum) return;
    try {
      const blob = await downloadFile({ fileId: fileIdNum }).unwrap();
      const fileName =
        fileInfo?.fileName || `payment-reminder-${fileIdNum}.xlsx`;
      downloadFileFromBlob(blob, fileName);
      showNotification('success', 'Success', 'File downloaded successfully');
    } catch (err) {
      showNotification(
        'error',
        'Error',
        extractErrorMessageFromAPI(err) || 'Failed to download file',
      );
    }
  };

  const handleExportAgingSummary = async () => {
    if (!fileIdNum) return;
    try {
      const blob = await exportAgingSummary({ fileId: fileIdNum }).unwrap();
      const fileName = `customer-aging-summary-${fileInfo?.requestNo || fileIdNum}.xlsx`;
      downloadFileFromBlob(blob, fileName);
      showNotification(
        'success',
        'Success',
        'Customer aging summary exported successfully',
      );
    } catch (err) {
      showNotification(
        'error',
        'Error',
        extractErrorMessageFromAPI(err) || 'Failed to export aging summary',
      );
    }
  };

  const handleUpdateFile = async () => {
    if (updateFileList.length === 0) {
      showNotification('error', 'Error', 'Please select a file to upload');
      return;
    }

    if (!fileIdNum) return;

    try {
      const formData = new FormData();
      formData.append('file', updateFileList[0].originFileObj);

      await updateFile({ fileId: fileIdNum, formData }).unwrap();
      showNotification('success', 'Success', 'File updated successfully');
      setIsUpdateModalOpen(false);
      setUpdateFileList([]);
      await refetchAgingSummary();
      window.location.reload();
    } catch (err) {
      showNotification(
        'error',
        'Error',
        extractErrorMessageFromAPI(err) || 'Failed to update file',
      );
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await downloadTemplate().unwrap();
      downloadFileFromBlob(blob, 'payment-reminder-template.xlsx');
      showNotification(
        'success',
        'Success',
        'Template downloaded successfully',
      );
    } catch (err) {
      showNotification(
        'error',
        'Error',
        extractErrorMessageFromAPI(err) || 'Failed to download template',
      );
    }
  };

  const updateUploadProps = {
    accept: '.xlsx,.xls,.csv',
    beforeUpload: (file: File) => {
      const isValidType =
        file.type ===
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel' ||
        file.type === 'text/csv' ||
        file.name.endsWith('.xlsx') ||
        file.name.endsWith('.xls') ||
        file.name.endsWith('.csv');

      if (!isValidType) {
        showNotification(
          'error',
          'Invalid File Type',
          'Please upload only Excel (.xlsx, .xls) or CSV files',
        );
        return Upload.LIST_IGNORE;
      }

      setUpdateFileList([
        { uid: file.name, name: file.name, originFileObj: file },
      ]);
      return false;
    },
    fileList: updateFileList,
    onRemove: () => {
      setUpdateFileList([]);
    },
  };

  const handleViewInvoices = (customerId: number) => {
    setSelectedCustomerId(customerId);
    setIsInvoicesSideSheetOpen(true);
  };

  const handleViewMailLogs = (customerId: number) => {
    setSelectedCustomerId(customerId);
    setIsMailLogsSideSheetOpen(true);
  };

  const handleCreateRemarks = (customerId: number) => {
    setSelectedCustomerId(customerId);
    setEditingRemark(null);
    remarkForm.reset({ remarks: '' });
    setIsRemarksModalOpen(true);
  };

  const handleEditRemark = (remark: Remark) => {
    setSelectedCustomerId(remark.customerId);
    setEditingRemark(remark);
    remarkForm.reset({ remarks: remark.remarks });
    setIsRemarksModalOpen(true);
  };

  const handleDeleteRemark = (remarkId: number) => {
    setRemarkToDelete(remarkId);
  };

  const handleDeleteRemarkConfirm = async () => {
    if (!remarkToDelete || !selectedCustomerId) return;
    try {
      await deleteRemark({ id: remarkToDelete }).unwrap();
      setRemarkToDelete(null);
      await refetchRemarks();
      refetchMailLogs();
      refetchAgingSummary();
      showNotification('success', 'Success', 'Remark deleted successfully');
    } catch (err) {
      const errorMessage = extractErrorMessageFromAPI(err);
      if (errorMessage && !errorMessage.toLowerCase().includes('success')) {
        showNotification(
          'error',
          'Error',
          errorMessage || 'Failed to delete remark',
        );
      }
    }
  };

  const handleSendEmail = (customerId: number) => {
    if (!fileIdNum) return;
    sendEmail({ fileId: fileIdNum, customerId })
      .unwrap()
      .then(() => {
        showNotification(
          'success',
          'Success',
          'Payment reminder email sent successfully',
        );

        refetchAgingSummary().catch(error => {
          console.error('Error refetching aging summary (immediate):', error);
        });

        setTimeout(() => {
          refetchAgingSummary().catch(error => {
            console.error(
              'Error refetching aging summary (delayed 1s):',
              error,
            );
          });
        }, 1000);

        setTimeout(() => {
          refetchAgingSummary().catch(error => {
            console.error(
              'Error refetching aging summary (delayed 2s):',
              error,
            );
          });
        }, 2000);

        if (isMailLogsSideSheetOpen && selectedCustomerId) {
          refetchMailLogs().catch(error => {
            console.error('Error refetching mail logs:', error);
          });
        }
      })
      .catch(err => {
        const errorMessage = extractErrorMessageFromAPI(err);
        if (errorMessage && !errorMessage.toLowerCase().includes('success')) {
          showNotification(
            'error',
            'Error',
            errorMessage || 'Failed to send email',
          );
        }
      });
  };

  const handleBulkSendEmail = () => {
    if (!fileIdNum || selectedCustomers.length === 0) return;
    bulkSendEmail({
      fileId: fileIdNum,
      customerIds: selectedCustomers,
    })
      .unwrap()
      .then(() => {
        showNotification(
          'success',
          'Success',
          `Payment reminder emails sent successfully to ${selectedCustomers.length} customer(s)`,
        );
        setSelectedCustomers([]);

        refetchAgingSummary().catch(error => {
          console.error('Error refetching aging summary (immediate):', error);
        });

        setTimeout(() => {
          refetchAgingSummary().catch(error => {
            console.error(
              'Error refetching aging summary (delayed 1s):',
              error,
            );
          });
        }, 1000);

        setTimeout(() => {
          refetchAgingSummary().catch(error => {
            console.error(
              'Error refetching aging summary (delayed 2s):',
              error,
            );
          });
        }, 2000);
      })
      .catch(err => {
        const errorMessage = extractErrorMessageFromAPI(err);
        if (errorMessage && !errorMessage.toLowerCase().includes('success')) {
          showNotification(
            'error',
            'Error',
            errorMessage || 'Failed to send bulk emails',
          );
        }
      });
  };

  const handleRowSelect = useCallback((customerId: number) => {
    setSelectedCustomers(prev => {
      if (prev.includes(customerId)) {
        return prev.filter(id => id !== customerId);
      }
      return [...prev, customerId];
    });
  }, []);

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        // Only select customers with actionable invoices (not just NOT_DUE)
        const actionableCustomerIds = customers
          .filter(customer =>
            Object.entries(customer.aging).some(
              ([bucketCode, amount]) => bucketCode !== 'NOT_DUE' && amount > 0,
            ),
          )
          .map(customer => customer.customerId);
        setSelectedCustomers(actionableCustomerIds);
      } else {
        setSelectedCustomers([]);
      }
    },
    [customers],
  );

  const handleRemarkSubmit = async (data: RemarkFormData) => {
    if (!selectedCustomerId || !fileIdNum) return;

    try {
      if (editingRemark) {
        await updateRemark({
          id: editingRemark.id,
          remarks: data.remarks,
        }).unwrap();
        showNotification('success', 'Success', 'Remark updated successfully');
      } else {
        await createRemark({
          fileId: fileIdNum,
          customerId: selectedCustomerId,
          remarks: data.remarks,
        }).unwrap();
        showNotification('success', 'Success', 'Remark created successfully');
      }

      await refetchRemarks();
      await refetchAgingSummary();
      setEditingRemark(null);
      remarkForm.reset({ remarks: '' });
    } catch (err) {
      showNotification(
        'error',
        'Error',
        extractErrorMessageFromAPI(err) || 'Failed to save remark',
      );
    }
  };

  /* ================= COLUMNS ================= */

  const columns = useMemo(() => {
    // Separate "Not Due" and "Getting Due" buckets from overdue buckets group
    // All buckets will be displayed, but "Overdue Buckets" label starts from OVERDUE_0_15 onwards
    const notDueBucket = buckets.find(b => b.bucketCode === 'NOT_DUE');
    const gettingDueBucket = buckets.find(b => b.bucketCode === 'GETTING_DUE');
    // Overdue buckets group starts from OVERDUE_0_15 onwards
    const overdueBuckets = buckets.filter(
      b => !['NOT_DUE', 'GETTING_DUE'].includes(b.bucketCode),
    );

    // Not Due column (standalone)
    const notDueColumn = notDueBucket
      ? columnHelper.accessor(row => row.aging[notDueBucket.bucketCode] || 0, {
          id: notDueBucket.bucketCode,
          header: () => {
            const config = getBucketColorConfig(notDueBucket.bucketCode);
            return (
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">
                  {notDueBucket.bucketLabel}
                </span>
              </div>
            );
          },
          cell: (info: CellContext<CustomerRow, number>) => {
            const value = info.getValue();
            const row = info.row.original;

            if (value > 0) {
              return (
                <button
                  onClick={() => {
                    setBucketInvoiceDetails({
                      customerId: row.customerId,
                      bucketCode: notDueBucket.bucketCode,
                    });
                    setBucketInvoicePage(1);
                  }}
                  className="font-medium text-emerald-700 hover:text-emerald-800 hover:underline cursor-pointer transition-colors"
                >
                  {formatAmountNoSymbol(value)}
                </button>
              );
            }
            return <span className="text-gray-400">-</span>;
          },
          size: 110,
        })
      : null;

    // Getting Due column (standalone)
    const gettingDueColumn = gettingDueBucket
      ? columnHelper.accessor(
          row => row.aging[gettingDueBucket.bucketCode] || 0,
          {
            id: gettingDueBucket.bucketCode,
            header: () => {
              const config = getBucketColorConfig(gettingDueBucket.bucketCode);
              return (
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                  <span className="text-[10px] font-bold text-orange-700 uppercase tracking-wider">
                    {gettingDueBucket.bucketLabel}
                  </span>
                </div>
              );
            },
            cell: (info: CellContext<CustomerRow, number>) => {
              const value = info.getValue();
              const row = info.row.original;

              if (value > 0) {
                return (
                  <button
                    onClick={() => {
                      setBucketInvoiceDetails({
                        customerId: row.customerId,
                        bucketCode: gettingDueBucket.bucketCode,
                      });
                      setBucketInvoicePage(1);
                    }}
                    className="font-medium text-orange-700 hover:text-orange-800 hover:underline cursor-pointer transition-colors text-xs"
                  >
                    {formatAmountNoSymbol(value)}
                  </button>
                );
              }
              return <span className="text-gray-400 text-xs">-</span>;
            },
            size: 110,
          },
        )
      : null;

    // Overdue bucket columns (starting from OVERDUE_0_15)
    const overdueBucketColumns = overdueBuckets.map(bucket => {
      const config = getBucketColorConfig(bucket.bucketCode);

      return columnHelper.accessor(row => row.aging[bucket.bucketCode] || 0, {
        id: bucket.bucketCode,
        header: () => {
          // Determine the color intensity based on bucket severity
          let dotColor = 'bg-red-400';
          if (bucket.bucketCode === 'OVERDUE_0_15') dotColor = 'bg-red-400';
          else if (bucket.bucketCode === 'OVERDUE_16_30')
            dotColor = 'bg-red-500';
          else if (bucket.bucketCode === 'OVERDUE_31_60')
            dotColor = 'bg-red-600';
          else if (bucket.bucketCode === 'OVERDUE_61_90')
            dotColor = 'bg-red-700';
          else if (bucket.bucketCode === 'OVERDUE_91_180')
            dotColor = 'bg-red-800';
          else if (bucket.bucketCode === 'OVERDUE_181_365')
            dotColor = 'bg-red-900';
          else if (bucket.bucketCode === 'OVERDUE_365_PLUS')
            dotColor = 'bg-red-950';

          return (
            <div className="flex items-center gap-1.5">
              <div className={`w-2 h-2 ${dotColor} rounded-full`}></div>
              <span
                className={`text-[10px] font-bold ${config.text} uppercase tracking-wider`}
              >
                {bucket.bucketLabel}
              </span>
            </div>
          );
        },
        cell: (info: CellContext<CustomerRow, number>) => {
          const value = info.getValue();
          const row = info.row.original;

          if (value > 0) {
            return (
              <button
                onClick={() => {
                  setBucketInvoiceDetails({
                    customerId: row.customerId,
                    bucketCode: bucket.bucketCode,
                  });
                  setBucketInvoicePage(1);
                }}
                className={`font-medium ${config.text} hover:opacity-80 hover:underline cursor-pointer transition-colors text-xs`}
              >
                {formatAmountNoSymbol(value)}
              </button>
            );
          }
          return <span className="text-gray-400 text-xs">-</span>;
        },
        size: 110,
      });
    });

    // Create grouped column for overdue buckets
    const overdueBucketsGroup: any =
      overdueBucketColumns.length > 0
        ? columnHelper.group({
            id: 'overdue-buckets',
            header: () => (
              <span className="text-[11px] font-extrabold text-red-600 uppercase tracking-wider">
                Overdue Buckets
              </span>
            ),
            columns: overdueBucketColumns as any,
          })
        : null;

    const checkboxColumn = columnHelper.display({
      id: 'select',
      header: () => {
        // Only count customers with actionable invoices for "select all"
        const actionableCustomers = customers.filter(customer =>
          Object.entries(customer.aging).some(
            ([bucketCode, amount]) => bucketCode !== 'NOT_DUE' && amount > 0,
          ),
        );

        const selectedActionableCount = selectedCustomers.filter(id =>
          actionableCustomers.some(c => c.customerId === id),
        ).length;

        return (
          <Checkbox
            onChange={e => handleSelectAll(e.target.checked)}
            checked={
              selectedActionableCount === actionableCustomers.length &&
              actionableCustomers.length > 0
            }
            indeterminate={
              selectedActionableCount > 0 &&
              selectedActionableCount < actionableCustomers.length
            }
            disabled={actionableCustomers.length === 0}
          />
        );
      },
      cell: (info: CellContext<CustomerRow, unknown>) => {
        const aging = info.row.original.aging;
        const hasActionableInvoices = Object.entries(aging).some(
          ([bucketCode, amount]) => bucketCode !== 'NOT_DUE' && amount > 0,
        );

        return (
          <Checkbox
            checked={selectedCustomers.includes(info.row.original.customerId)}
            onChange={() => handleRowSelect(info.row.original.customerId)}
            disabled={!hasActionableInvoices}
          />
        );
      },
      size: 40,
    });

    const actionColumn = columnHelper.display({
      id: 'actions',
      header: () => (
        <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">
          Actions
        </span>
      ),
      cell: (info: CellContext<CustomerRow, unknown>) => {
        const customerId = info.row.original.customerId;
        const menuItems: MenuProps['items'] = [
          {
            key: 'view-invoices',
            label: 'View Invoices',
            onClick: () => handleViewInvoices(customerId),
          },
          {
            key: 'view-mail-logs',
            label: 'Mail Logs',
            onClick: () => handleViewMailLogs(customerId),
          },
          {
            key: 'remarks',
            label: 'Remarks',
            onClick: () => handleCreateRemarks(customerId),
          },
        ];
        return (
          <Dropdown menu={{ items: menuItems }} trigger={['click']}>
            <EllipsisVerticalIcon className="size-4 cursor-pointer stroke-[2.5px] text-gray-500 hover:text-indigo-600 transition-colors" />
          </Dropdown>
        );
      },
      size: 40,
    });

    const customerColumn = columnHelper.accessor('customerName', {
      header: () => (
        <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">
          Customer
        </span>
      ),
      cell: (info: CellContext<CustomerRow, string>) => {
        const recentRemark = info.row.original.recentRemark;

        return (
          <div>
            <div className="flex items-center gap-2">
              <div className="font-semibold text-gray-900 text-sm">
                {info.getValue()}
              </div>
              {recentRemark && (
                <Tooltip
                  title={
                    <div>
                      <div className="text-xs font-medium mb-1">
                        Recent Remark:
                      </div>
                      <div className="text-xs">{recentRemark.remarks}</div>
                      <div className="text-xs text-gray-400 mt-1">
                        {formatDayJsDate(
                          dayjs(recentRemark.createdAt),
                          'verbose',
                        )}
                      </div>
                    </div>
                  }
                >
                  <Badge
                    count="R"
                    style={{ backgroundColor: '#10b981', cursor: 'pointer' }}
                  />
                </Tooltip>
              )}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {info.row.original.customerNumber}
            </div>
          </div>
        );
      },
      size: 200,
    });

    const totalInvoiceAmountColumn = columnHelper.accessor(
      'totalInvoiceAmount',
      {
        header: () => (
          <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">
            Total Invoice Amount (INR)
          </span>
        ),
        cell: (info: CellContext<CustomerRow, number>) => (
          <span className="font-bold text-gray-900 text-sm">
            {formatAmountNoSymbol(info.getValue() ?? 0)}
          </span>
        ),
        size: 150,
      },
    );

    const sendEmailColumn = columnHelper.display({
      id: 'send-email',
      header: () => (
        <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">
          Send Email
        </span>
      ),
      cell: (info: CellContext<CustomerRow, unknown>) => {
        const customerId = info.row.original.customerId;
        const lastMailSentAt = info.row.original.lastMailSentAt;
        const aging = info.row.original.aging;

        // Check if customer has any amounts in buckets other than NOT_DUE
        const hasActionableInvoices = Object.entries(aging).some(
          ([bucketCode, amount]) => bucketCode !== 'NOT_DUE' && amount > 0,
        );

        // Don't show button if only NOT_DUE invoices exist
        if (!hasActionableInvoices) {
          return (
            <div className="flex items-center justify-end">
              <span className="text-[10px] text-gray-400 italic">
                No action needed
              </span>
            </div>
          );
        }

        if (lastMailSentAt) {
          return (
            <div className="flex flex-col items-end gap-1">
              <Tooltip title="Retrigger email">
                <button
                  onClick={() => handleSendEmail(customerId)}
                  disabled={isSendingEmail}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
                >
                  <RefreshCw className="w-3 h-3" />
                  Resend
                </button>
              </Tooltip>
              <span className="text-[10px] text-gray-500">
                {formatDayJsDate(dayjs(lastMailSentAt), 'Do MMM, YY')}
              </span>
            </div>
          );
        }

        return (
          <div className="flex items-center justify-end">
            <Tooltip title="Send payment reminder">
              <button
                onClick={() => handleSendEmail(customerId)}
                disabled={isSendingEmail}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
              >
                <Send className="w-3 h-3" />
                Send
              </button>
            </Tooltip>
          </div>
        );
      },
      size: 113,
    });

    const recentRemarksColumn = columnHelper.display({
      id: 'recent-remarks',
      header: () => (
        <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">
          Recent Remarks
        </span>
      ),
      cell: (info: CellContext<CustomerRow, unknown>) => {
        const recentRemark = info.row.original.recentRemark;

        if (recentRemark) {
          return (
            <div className="max-w-[250px]">
              <div className="text-xs text-gray-700 line-clamp-2 leading-relaxed">
                {recentRemark.remarks}
              </div>
              <div className="text-[10px] text-gray-500 mt-1">
                {formatDayJsDate(dayjs(recentRemark.createdAt), 'Do MMM, YY')}
              </div>
            </div>
          );
        }

        return <span className="text-gray-400 text-xs">No remarks</span>;
      },
      size: 132,
    });

    // Build final column array
    const finalColumns: any[] = [checkboxColumn, actionColumn, customerColumn];

    // Add Not Due column if exists
    if (notDueColumn) {
      // @ts-ignore - Type compatibility with column groups
      finalColumns.push(notDueColumn);
    }

    // Add Getting Due column if exists
    if (gettingDueColumn) {
      // @ts-ignore - Type compatibility with column groups
      finalColumns.push(gettingDueColumn);
    }

    // Add Overdue Buckets group if exists (starts from OVERDUE_0_15)
    if (overdueBucketsGroup) {
      finalColumns.push(overdueBucketsGroup);
    }

    // Add remaining columns
    // @ts-ignore - Type compatibility with column groups
    finalColumns.push(
      totalInvoiceAmountColumn,
      recentRemarksColumn,
      sendEmailColumn,
    );

    return finalColumns;
  }, [
    buckets,
    isSendingEmail,
    selectedCustomers,
    handleSelectAll,
    handleRowSelect,
  ]);

  const table = useReactTable({
    data: customers,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableColumnPinning: true,
    manualPagination: true,
    initialState: {
      columnPinning: {
        left: ['select', 'actions'],
        right: ['send-email'],
      },
    },
  });

  // Mail Logs Table
  const mailLogColumnHelper = createColumnHelper<MailLog>();
  const mailLogColumns = useMemo(
    () => [
      mailLogColumnHelper.display({
        id: 'serialNo',
        header: () => (
          <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">
            #
          </span>
        ),
        cell: info => (
          <span className="text-xs text-gray-600 font-medium">
            {info.row.index + 1}
          </span>
        ),
        size: 50,
      }),
      mailLogColumnHelper.accessor('customerEmail', {
        header: () => (
          <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">
            To
          </span>
        ),
        cell: info => (
          <span className="text-gray-600 text-xs">
            {info.getValue() || '-'}
          </span>
        ),
      }),
      mailLogColumnHelper.display({
        id: 'ccEmails',
        header: () => (
          <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">
            CC
          </span>
        ),
        cell: info => {
          const ccEmails = info.row.original.ccEmails;
          if (!ccEmails || (Array.isArray(ccEmails) && ccEmails.length === 0)) {
            return <span className="text-gray-600 text-xs">-</span>;
          }
          return (
            <span className="text-gray-600 text-xs">
              {Array.isArray(ccEmails) ? ccEmails.join(', ') : String(ccEmails)}
            </span>
          );
        },
      }),
      mailLogColumnHelper.accessor('status', {
        header: () => (
          <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">
            Status
          </span>
        ),
        cell: info => (
          <Badge
            status={info.getValue() === 'SENT' ? 'success' : 'default'}
            text={
              <span className="text-xs font-medium">
                {info.getValue() || '-'}
              </span>
            }
          />
        ),
      }),
      mailLogColumnHelper.accessor('sentAt', {
        header: () => (
          <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">
            Sent At
          </span>
        ),
        cell: info => {
          const sentAt = info.getValue();
          return (
            <span className="text-gray-600 text-xs">
              {sentAt ? dayjs(sentAt).format('DD MMM YY, hh:mm A') : '-'}
            </span>
          );
        },
      }),
      mailLogColumnHelper.display({
        id: 'sentBy',
        header: () => (
          <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">
            Sent By
          </span>
        ),
        cell: info => {
          const sentBy = info.row.original.sentBy;
          return (
            <span className="text-gray-600 text-xs">{sentBy?.name || '-'}</span>
          );
        },
      }),
    ],
    [],
  );

  const mailLogTable = useReactTable({
    data: mailLogs,
    columns: mailLogColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Invoice Table (side sheet)
  const invoiceColumnHelper = createColumnHelper<Invoice>();
  const invoiceColumns = useMemo(
    () => [
      invoiceColumnHelper.accessor('invoiceNumber', {
        header: () => (
          <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">
            Invoice #
          </span>
        ),
        cell: info => (
          <div>
            <div className="font-semibold text-gray-900 text-sm">
              {info.getValue() || '-'}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              SAP: {info.row.original.sapDocumentNumber || '-'}
            </div>
          </div>
        ),
      }),
      invoiceColumnHelper.accessor('invoiceDate', {
        header: () => (
          <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">
            Invoice Date
          </span>
        ),
        cell: info => (
          <span className="text-gray-600 text-xs">
            {info.getValue()
              ? formatDayJsDate(dayjs(info.getValue()), 'Do MMM, YY')
              : '-'}
          </span>
        ),
      }),
      invoiceColumnHelper.accessor('dueDate', {
        header: () => (
          <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">
            Due Date
          </span>
        ),
        cell: info => (
          <span className="text-gray-600 text-xs">
            {info.getValue()
              ? formatDayJsDate(dayjs(info.getValue()), 'Do MMM, YY')
              : '-'}
          </span>
        ),
      }),
      invoiceColumnHelper.accessor('invoiceAmount', {
        header: () => (
          <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">
            Invoice Amount
          </span>
        ),
        cell: info => {
          const amount = info.getValue();
          const currency = info.row.original.currency;
          const amountStr =
            amount != null && amount !== ''
              ? formatAmountNoSymbol(amount)
              : null;
          return (
            <span className="font-semibold text-gray-900 text-sm">
              {amountStr ?? '-'}
              {amountStr != null && currency && (
                <span className="text-[10px] text-gray-500 font-normal ml-1">
                  {currency}
                </span>
              )}
            </span>
          );
        },
      }),
      invoiceColumnHelper.accessor(row => row.invoiceAmountInr ?? null, {
        id: 'invoiceAmountInr',
        header: () => (
          <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">
            Invoice Amount (INR)
          </span>
        ),
        cell: info => (
          <span className="font-semibold text-gray-900 text-sm">
            {info.getValue() != null && info.getValue() !== ''
              ? formatAmountNoSymbol(info.getValue())
              : '-'}
          </span>
        ),
      }),
    ],
    [],
  );

  // Memoize invoices data to prevent unnecessary table recreations
  const memoizedInvoices = useMemo(() => {
    return invoices.map((inv: any) => ({
      ...inv,
      customerId: inv.customer?.id || inv.customerId,
      customerNumber: inv.customer?.customerNumber || '',
      invoiceAmount: inv.invoiceAmount ?? inv.grossAmount,
      invoiceAmountInr: inv.invoiceAmountInr ?? null,
      currency: inv.currency ?? null,
    })) as Invoice[];
  }, [invoices]);

  const invoiceTable = useReactTable({
    data: memoizedInvoices,
    columns: invoiceColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  // All Invoices Table (main view)
  const allInvoicesColumnHelper = createColumnHelper<Invoice>();
  const allInvoicesColumns = useMemo(
    () => [
      allInvoicesColumnHelper.accessor('customer.customerName', {
        header: () => (
          <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">
            Customer
          </span>
        ),
        cell: info => (
          <div>
            <div className="font-semibold text-gray-900 text-sm">
              {info.getValue() || '-'}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {info.row.original.customerNumber ||
                info.row.original.customer?.customerNumber ||
                '-'}
            </div>
          </div>
        ),
        size: 200,
      }),
      allInvoicesColumnHelper.accessor('invoiceNumber', {
        header: () => (
          <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">
            Invoice #
          </span>
        ),
        cell: info => (
          <div>
            <div className="font-semibold text-gray-900 text-sm">
              {info.getValue() || '-'}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              SAP: {info.row.original.sapDocumentNumber || '-'}
            </div>
          </div>
        ),
        size: 180,
      }),
      allInvoicesColumnHelper.accessor('invoiceDate', {
        header: () => (
          <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">
            Invoice Date
          </span>
        ),
        cell: info => (
          <span className="text-gray-600 text-xs">
            {info.getValue()
              ? formatDayJsDate(dayjs(info.getValue()), 'Do MMM, YYYY')
              : '-'}
          </span>
        ),
        size: 130,
      }),
      allInvoicesColumnHelper.accessor('dueDate', {
        header: () => (
          <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">
            Due Date
          </span>
        ),
        cell: info => (
          <span className="text-gray-600 text-xs">
            {info.getValue()
              ? formatDayJsDate(dayjs(info.getValue()), 'Do MMM, YYYY')
              : '-'}
          </span>
        ),
        size: 130,
      }),
      allInvoicesColumnHelper.accessor('invoiceAmount', {
        header: () => (
          <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">
            Invoice Amount
          </span>
        ),
        cell: info => {
          const amount = info.getValue();
          const currency = info.row.original.currency;
          const amountStr =
            amount != null && amount !== ''
              ? formatAmountNoSymbol(amount)
              : null;
          return (
            <span className="font-semibold text-gray-900 text-sm">
              {amountStr ?? '-'}
              {amountStr != null && currency && (
                <span className="text-[10px] text-gray-500 font-normal ml-1">
                  {currency}
                </span>
              )}
            </span>
          );
        },
        size: 130,
      }),
      allInvoicesColumnHelper.accessor(row => row.invoiceAmountInr ?? null, {
        id: 'invoiceAmountInr',
        header: () => (
          <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">
            Invoice Amount (INR)
          </span>
        ),
        cell: info => (
          <span className="font-semibold text-gray-900 text-sm">
            {info.getValue() != null && info.getValue() !== ''
              ? formatAmountNoSymbol(info.getValue())
              : '-'}
          </span>
        ),
        size: 130,
      }),
      allInvoicesColumnHelper.accessor('agingDays', {
        header: () => (
          <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">
            Aging Days
          </span>
        ),
        cell: info => {
          const agingDays = info.getValue();
          if (agingDays === undefined || agingDays === null)
            return <span className="text-gray-400 text-xs">-</span>;

          // Color code based on aging
          let colorClass = 'text-gray-600';
          if (agingDays < 0)
            colorClass = 'text-emerald-600'; // Not due
          else if (agingDays <= 15)
            colorClass = 'text-orange-600'; // Due soon or recently overdue
          else if (agingDays <= 60)
            colorClass = 'text-red-600'; // Moderately overdue
          else colorClass = 'text-red-900'; // Critically overdue

          return (
            <span className={`${colorClass} text-xs font-medium`}>
              {agingDays > 0
                ? `${agingDays} days`
                : agingDays < 0
                  ? `${Math.abs(agingDays)} days`
                  : 'Due today'}
            </span>
          );
        },
        size: 100,
      }),
      allInvoicesColumnHelper.accessor('statusLabel', {
        header: () => (
          <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">
            Status
          </span>
        ),
        cell: info => {
          const statusLabel = info.getValue();
          const statusColor = info.row.original.statusColor;

          if (!statusLabel)
            return <span className="text-gray-400 text-xs">-</span>;

          return (
            <Badge
              color={statusColor || 'default'}
              text={statusLabel}
              className="text-[10px]"
            />
          );
        },
        size: 100,
      }),
    ],
    [],
  );

  const allInvoicesTable = useReactTable({
    data: allInvoices,
    columns: allInvoicesColumns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  });

  /* ================= UI ================= */

  const showRefreshLoader =
    isManualRefresh &&
    ((viewMode === 'customerBucket' && isFetchingAgingSummary) ||
      (viewMode === 'invoices' && isFetchingAllInvoices));

  if (isLoadingAgingSummary) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <CardWizard addOnClass="p-6">
          <Skeleton active paragraph={{ rows: 10 }} />
        </CardWizard>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <GlobalLoader
        loading={showRefreshLoader || false}
        message="Refreshing data..."
      />

      {/* Modern Compact Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-3.5">
          {/* Top Row: Title + Actions */}
          <div className="flex items-center justify-between mb-3">
            {/* Title Section */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/payment-reminder/all')}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4 text-gray-600" />
              </button>
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg shadow-md">
                  <FileSpreadsheet className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-gray-900">
                    {fileInfo?.requestNo || 'Payment Reminder'}
                  </h1>
                  {fileInfo && (
                    <div className="flex items-center gap-2 text-[11px] text-gray-500 mt-0.5">
                      <span>
                        {agingSummaryData?.file?.uploadedBy?.name ||
                          (fileInfo as any)?.audit?.uploadedBy?.name ||
                          'N/A'}
                      </span>
                      <span className="text-gray-300">•</span>
                      <span>
                        {fileInfo.uploadedAt
                          ? dayjs(fileInfo.uploadedAt).format('DD MMM YYYY')
                          : '-'}
                      </span>
                      {(fileInfo as any)?.updatedAt && (
                        <>
                          <span className="text-gray-300">•</span>
                          <span className="text-emerald-600 font-medium">
                            Updated
                          </span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons - Modern B2B SaaS Style */}
            <div className="flex items-center gap-2">
              <Tooltip title="Refresh data">
                <button
                  onClick={handleRefresh}
                  disabled={
                    viewMode === 'customerBucket'
                      ? isFetchingAgingSummary
                      : isFetchingAllInvoices
                  }
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                >
                  <RotateCcw
                    className={`w-4 h-4 text-gray-600 ${
                      (viewMode === 'customerBucket' &&
                        isFetchingAgingSummary) ||
                      (viewMode === 'invoices' && isFetchingAllInvoices)
                        ? 'animate-spin'
                        : ''
                    }`}
                  />
                </button>
              </Tooltip>

              <div className="w-px h-6 bg-gray-200" />

              <button
                onClick={handleExportAgingSummary}
                disabled={isExportingAgingSummary}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg transition-all text-xs font-medium disabled:opacity-50"
              >
                <DownloadIcon className="w-3.5 h-3.5" />
                Export Summary
              </button>

              <button
                onClick={() => setIsUpdateModalOpen(true)}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg transition-all text-xs font-medium"
              >
                <FileUp className="w-3.5 h-3.5" />
                Re-upload
              </button>

              <button
                onClick={handleDownloadFile}
                disabled={isDownloadingFile}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-all text-xs font-medium shadow-sm disabled:opacity-50"
              >
                <FileSpreadsheet className="w-3.5 h-3.5" />
                Download File
              </button>
            </div>
          </div>

          {/* Tabs Row */}
          <Tabs
            activeKey={viewMode}
            onChange={key => {
              setViewMode(key as 'customerBucket' | 'invoices');
              setInvoicePage(1);
              setInvoiceSearch('');
              setSelectedCustomers([]);
            }}
            size="small"
            className="payment-reminder-tabs"
            items={[
              {
                key: 'customerBucket',
                label: (
                  <span className="text-xs font-semibold">Customer Aging</span>
                ),
              },
              {
                key: 'invoices',
                label: (
                  <span className="text-xs font-semibold">All Invoices</span>
                ),
              },
            ]}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-4">
        <CardWizard addOnClass="p-0 shadow-sm border border-gray-200">
          {viewMode === 'customerBucket' ? (
            <div className="p-4">
              {/* Aging Summary Dashboard */}
              {buckets.length > 0 && customers.length > 0 && (
                <AgingSummaryDashboard
                  buckets={buckets}
                  customers={customers}
                />
              )}

              {/* Compact Toolbar */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <BucketColorLegend buckets={buckets} />
                  <Select
                    value={customerSortOrder}
                    onChange={value => {
                      setCustomerSortOrder(value);
                      setCustomerPage(1);
                    }}
                    style={{ width: 90 }}
                    size="small"
                    className="text-xs"
                    options={[
                      { label: 'A-Z', value: 'asc' },
                      { label: 'Z-A', value: 'desc' },
                    ]}
                    suffixIcon={
                      <SwapOutlined
                        rotate={90}
                        style={{ color: '#6B7280', fontSize: 10 }}
                      />
                    }
                  />
                  <Input
                    allowClear
                    prefix={
                      <SearchOutlined className="text-gray-400 text-xs" />
                    }
                    placeholder="Search customers..."
                    className="w-[220px]"
                    size="small"
                    value={customerSearch}
                    onChange={e => {
                      setCustomerSearch(e.target.value);
                      setCustomerPage(1);
                    }}
                  />
                  <div className="text-xs text-gray-500 ml-2">
                    {customersTotal} customer{customersTotal !== 1 ? 's' : ''}
                  </div>
                </div>

                {selectedCustomers.length > 0 && (
                  <button
                    onClick={handleBulkSendEmail}
                    disabled={isBulkSendingEmail}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-all text-xs font-medium shadow-sm disabled:opacity-50"
                  >
                    <Send className="w-3.5 h-3.5" />
                    Send to {selectedCustomers.length} Customer
                    {selectedCustomers.length > 1 ? 's' : ''}
                  </button>
                )}
              </div>

              {/* Custom Table with Grouped Headers */}
              <div className="flex flex-col">
                <div className="relative overflow-x-auto shadow border border-gray-200 min-h-[calc(100vh-10rem)] max-h-[calc(100vh-10rem)]">
                  <table className="min-w-full text-sm text-gray-800">
                    <thead className="sticky top-0 z-[11] shadow-sm border-b border-gray-300 bg-[#F4F2FF]">
                      {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map(header => {
                            const colSpan = header.colSpan || 1;
                            const isGroupHeader =
                              header.isPlaceholder === false &&
                              header.subHeaders &&
                              header.subHeaders.length > 0;
                            const pinningStyles =
                              getCommonColumnPinningStylesForHeader(
                                header.column,
                                table,
                              );

                            return (
                              <th
                                key={header.id}
                                colSpan={colSpan}
                                className={`px-4 py-3 text-sm font-semibold text-left whitespace-nowrap text-[#6E6893] border-r border-gray-300 last:border-r-0 ${
                                  isGroupHeader
                                    ? 'border-b border-gray-300 text-center'
                                    : ''
                                }`}
                                style={{
                                  ...pinningStyles,
                                  backgroundColor: isGroupHeader
                                    ? '#fef2f2'
                                    : '#F4F2FF',
                                }}
                              >
                                {header.isPlaceholder
                                  ? null
                                  : flexRender(
                                      header.column.columnDef.header,
                                      header.getContext(),
                                    )}
                              </th>
                            );
                          })}
                        </tr>
                      ))}
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {table.getRowModel().rows.map(row => (
                        <tr
                          key={row.id}
                          className="hover:bg-gray-50 transition-colors duration-150"
                        >
                          {row.getVisibleCells().map(cell => {
                            const pinningStyles = getCommonColumnPinningStyles(
                              cell.column,
                              table,
                            );

                            return (
                              <td
                                key={cell.id}
                                className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap font-medium tracking-normal border-b border-gray-200 text-left"
                                style={pinningStyles}
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext(),
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-between bg-[#F2F0F9] items-center px-4 py-2.5 text-[#6E6893]">
                  <div className="text-xs">
                    Showing {(customerPage - 1) * customerPageSize + 1} to{' '}
                    {Math.min(customerPage * customerPageSize, customersTotal)}{' '}
                    of {customersTotal} customers
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() =>
                        setCustomerPage(prev => Math.max(1, prev - 1))
                      }
                      disabled={customerPage === 1}
                      className="px-3 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="text-xs">
                      Page {customerPage} of{' '}
                      {Math.ceil(customersTotal / customerPageSize)}
                    </span>
                    <button
                      onClick={() =>
                        setCustomerPage(prev =>
                          Math.min(
                            Math.ceil(customersTotal / customerPageSize),
                            prev + 1,
                          ),
                        )
                      }
                      disabled={
                        customerPage >=
                        Math.ceil(customersTotal / customerPageSize)
                      }
                      className="px-3 py-1 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                    <select
                      value={customerPageSize}
                      onChange={e => {
                        setCustomerPageSize(Number(e.target.value));
                        setCustomerPage(1);
                      }}
                      className="px-2 py-1 text-xs bg-white border border-gray-300 rounded"
                    >
                      <option value={25}>25 / page</option>
                      <option value={50}>50 / page</option>
                      <option value={100}>100 / page</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4">
              {/* Compact Filters */}
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <Input
                  allowClear
                  prefix={<SearchOutlined className="text-gray-400 text-xs" />}
                  placeholder="Search invoices..."
                  className="w-[200px]"
                  size="small"
                  value={invoiceSearch}
                  onChange={e => {
                    setInvoiceSearch(e.target.value);
                    setInvoicePage(1);
                  }}
                />

                <Select
                  allowClear
                  placeholder="Customer"
                  className="w-[180px]"
                  size="small"
                  value={invoiceCustomerId}
                  onChange={value => {
                    setInvoiceCustomerId(value);
                    setInvoicePage(1);
                  }}
                  loading={isLoadingCustomersDropdown}
                  options={
                    customersDropdownData?.data?.map(customer => ({
                      label: customer.label,
                      value: customer.id,
                    })) || []
                  }
                />

                <DatePicker
                  placeholder="Invoice From"
                  className="w-[135px]"
                  size="small"
                  value={invoiceDateFrom}
                  onChange={date => {
                    setInvoiceDateFrom(date);
                    setInvoicePage(1);
                  }}
                  format="DD MMM YYYY"
                />

                <DatePicker
                  placeholder="Invoice To"
                  className="w-[135px]"
                  size="small"
                  value={invoiceDateTo}
                  onChange={date => {
                    setInvoiceDateTo(date);
                    setInvoicePage(1);
                  }}
                  format="DD MMM YYYY"
                />

                <DatePicker
                  placeholder="Due From"
                  className="w-[135px]"
                  size="small"
                  value={dueDateFrom}
                  onChange={date => {
                    setDueDateFrom(date);
                    setInvoicePage(1);
                  }}
                  format="DD MMM YYYY"
                />

                <DatePicker
                  placeholder="Due To"
                  className="w-[135px]"
                  size="small"
                  value={dueDateTo}
                  onChange={date => {
                    setDueDateTo(date);
                    setInvoicePage(1);
                  }}
                  format="DD MMM YYYY"
                />

                <Select
                  placeholder="Sort By"
                  className="w-[160px]"
                  size="small"
                  value={invoiceSortBy}
                  onChange={value => {
                    setInvoiceSortBy(value);
                    setInvoicePage(1);
                  }}
                  options={[
                    {
                      label: 'Latest Recent',
                      value: PaymentReminderInvoiceSortBy.LATEST_RECENT_DATE,
                    },
                    {
                      label: 'Oldest Recent',
                      value: PaymentReminderInvoiceSortBy.OLDEST_RECENT_DATE,
                    },
                    {
                      label: 'Invoice (Latest)',
                      value: PaymentReminderInvoiceSortBy.INVOICE_DATE_LATEST,
                    },
                    {
                      label: 'Invoice (Oldest)',
                      value: PaymentReminderInvoiceSortBy.INVOICE_DATE_OLDEST,
                    },
                    {
                      label: 'Due (Latest)',
                      value: PaymentReminderInvoiceSortBy.DUE_DATE_LATEST,
                    },
                    {
                      label: 'Due (Oldest)',
                      value: PaymentReminderInvoiceSortBy.DUE_DATE_OLDEST,
                    },
                  ]}
                />

                {(invoiceSearch ||
                  invoiceCustomerId ||
                  invoiceDateFrom ||
                  invoiceDateTo ||
                  dueDateFrom ||
                  dueDateTo) && (
                  <button
                    onClick={() => {
                      setInvoiceSearch('');
                      setInvoiceCustomerId(undefined);
                      setInvoiceDateFrom(null);
                      setInvoiceDateTo(null);
                      setDueDateFrom(null);
                      setDueDateTo(null);
                      setInvoicePage(1);
                    }}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <X className="w-3 h-3" />
                    Clear
                  </button>
                )}

                <div className="ml-auto text-xs text-gray-500">
                  {allInvoicesTotal} invoice{allInvoicesTotal !== 1 ? 's' : ''}
                </div>
              </div>

              {/* Table */}
              {isLoadingAllInvoices && !allInvoicesResponse ? (
                <Skeleton active paragraph={{ rows: 10 }} />
              ) : (
                <CommonTravelTable<Invoice, any>
                  table={allInvoicesTable}
                  total={allInvoicesTotal}
                  appliedFilters={{ page: invoicePage, pageSize: invoiceLimit }}
                  setAppliedFilters={(filtersOrUpdater: any) => {
                    const newFilters =
                      typeof filtersOrUpdater === 'function'
                        ? filtersOrUpdater({
                            page: invoicePage,
                            pageSize: invoiceLimit,
                          })
                        : filtersOrUpdater;

                    if (
                      newFilters.page !== undefined &&
                      newFilters.page !== invoicePage
                    ) {
                      setInvoicePage(newFilters.page);
                    }
                    if (
                      newFilters.pageSize !== undefined &&
                      newFilters.pageSize !== invoiceLimit
                    ) {
                      setInvoiceLimit(newFilters.pageSize);
                      setInvoicePage(1);
                    }
                  }}
                />
              )}
            </div>
          )}
        </CardWizard>
      </div>

      {/* Invoices SideSheet */}
      <AnimatePresence>
        {isInvoicesSideSheetOpen && (
          <SideSheet
            isOpen={isInvoicesSideSheetOpen}
            onClose={() => {
              setIsInvoicesSideSheetOpen(false);
              setSelectedCustomerId(null);
              setCustomerInvoicePage(1);
              setCustomerInvoicePageSize(25);
            }}
            addOnClass="w-[55vw]"
            headerChildren={
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100">
                <div className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg shadow-md">
                      <FileText className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">
                        Customer Invoices
                      </h2>
                      <p className="text-xs text-gray-600 mt-0.5">
                        View all invoices for selected customer
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setIsInvoicesSideSheetOpen(false);
                        setSelectedCustomerId(null);
                        setCustomerInvoicePage(1);
                        setCustomerInvoicePageSize(25);
                      }}
                      className="ml-auto p-2 hover:bg-white/60 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                </div>
              </div>
            }
            contentChildren={
              <div className="p-6 bg-gray-50">
                {isLoadingInvoices ? (
                  <div className="bg-white rounded-lg p-6 shadow-sm">
                    <Skeleton active paragraph={{ rows: 5 }} />
                  </div>
                ) : invoices.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 bg-white rounded-lg shadow-sm">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <FileText className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 mb-1">
                      No Invoices Found
                    </h3>
                    <p className="text-sm text-gray-500">
                      This customer doesn't have any invoices yet
                    </p>
                  </div>
                ) : (
                  <div
                    className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col"
                    style={{ height: 'calc(100vh - 20rem)' }}
                  >
                    <div
                      className="overflow-x-auto flex-1"
                      style={{ overflowY: 'auto', minHeight: 0 }}
                    >
                      <table className="w-full">
                        <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                          {invoiceTable.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                              {headerGroup.headers.map(header => (
                                <th
                                  className="px-4 py-3 text-left border-b border-gray-200"
                                  key={header.id}
                                >
                                  {header.isPlaceholder
                                    ? null
                                    : flexRender(
                                        header.column.columnDef.header,
                                        header.getContext(),
                                      )}
                                </th>
                              ))}
                            </tr>
                          ))}
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {invoiceTable.getRowModel().rows.map((row, idx) => (
                            <tr
                              key={row.id}
                              className={`${
                                idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                              } hover:bg-indigo-50/30 transition-colors`}
                            >
                              {row.getVisibleCells().map(cell => (
                                <td className="px-4 py-3" key={cell.id}>
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext(),
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Pagination */}
                    <div
                      className="flex justify-between bg-gray-50 items-center px-4 py-2.5 border-t border-gray-200"
                      style={{
                        position: 'relative',
                        zIndex: 50,
                        pointerEvents: 'auto',
                      }}
                    >
                      <Pagination
                        size="small"
                        total={invoicesTotal}
                        pageSize={customerInvoicePageSize}
                        current={customerInvoicePage}
                        pageSizeOptions={[25, 50, 100, 200]}
                        showSizeChanger
                        showTotal={total => (
                          <span className="text-xs text-gray-600 font-semibold">
                            Total {total} items
                          </span>
                        )}
                        onChange={page => {
                          setCustomerInvoicePage(page);
                        }}
                        onShowSizeChange={(_current, size) => {
                          setCustomerInvoicePage(1);
                          setCustomerInvoicePageSize(size);
                        }}
                        className="text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            }
          />
        )}
      </AnimatePresence>

      {/* Mail Logs SideSheet */}
      <AnimatePresence>
        {isMailLogsSideSheetOpen && (
          <SideSheet
            isOpen={isMailLogsSideSheetOpen}
            onClose={() => {
              setIsMailLogsSideSheetOpen(false);
              setSelectedCustomerId(null);
            }}
            addOnClass="w-[60vw]"
            headerChildren={
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b border-blue-100">
                <div className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg shadow-md">
                      <Inbox className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">
                        Email Activity Logs
                      </h2>
                      <p className="text-xs text-gray-600 mt-0.5">
                        Complete history of payment reminder emails
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setIsMailLogsSideSheetOpen(false);
                        setSelectedCustomerId(null);
                      }}
                      className="ml-auto p-2 hover:bg-white/60 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                </div>
              </div>
            }
            contentChildren={
              <div className="p-6 bg-gray-50">
                <div className="mb-4 flex justify-end">
                  <Select
                    value={mailLogSortBy}
                    onChange={setMailLogSortBy}
                    style={{ width: 180 }}
                    size="small"
                    placeholder="Sort by"
                    allowClear
                    suffixIcon={
                      <SwapOutlined
                        rotate={90}
                        style={{ color: '#6B7280', fontSize: 12 }}
                      />
                    }
                    options={[
                      {
                        label: 'Created (Recent)',
                        value: PaymentReminderMailLogSortBy.CREATED_DATE_DESC,
                      },
                      {
                        label: 'Created (Oldest)',
                        value: PaymentReminderMailLogSortBy.CREATED_DATE_ASC,
                      },
                      {
                        label: 'Sent (Recent)',
                        value: PaymentReminderMailLogSortBy.SENT_DATE_DESC,
                      },
                      {
                        label: 'Sent (Oldest)',
                        value: PaymentReminderMailLogSortBy.SENT_DATE_ASC,
                      },
                      {
                        label: 'Status (A-Z)',
                        value: PaymentReminderMailLogSortBy.STATUS_ASC,
                      },
                      {
                        label: 'Status (Z-A)',
                        value: PaymentReminderMailLogSortBy.STATUS_DESC,
                      },
                    ]}
                  />
                </div>
                {isMailLogsLoading ? (
                  <div className="bg-white rounded-lg p-6 shadow-sm">
                    <Skeleton active paragraph={{ rows: 5 }} />
                  </div>
                ) : mailLogs.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 bg-white rounded-lg shadow-sm">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <Inbox className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 mb-1">
                      No Email Activity Yet
                    </h3>
                    <p className="text-sm text-gray-500 text-center max-w-sm">
                      No payment reminder emails have been sent to this customer
                    </p>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div
                      className="overflow-x-auto"
                      style={{ maxWidth: '100%' }}
                    >
                      <table className="w-full" style={{ minWidth: '800px' }}>
                        <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                          {mailLogTable.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                              {headerGroup.headers.map(header => (
                                <th
                                  className="px-4 py-3 text-left border-b border-gray-200"
                                  key={header.id}
                                >
                                  {header.isPlaceholder
                                    ? null
                                    : flexRender(
                                        header.column.columnDef.header,
                                        header.getContext(),
                                      )}
                                </th>
                              ))}
                            </tr>
                          ))}
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {mailLogTable.getRowModel().rows.map((row, idx) => (
                            <tr
                              key={row.id}
                              className={`${
                                idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                              } hover:bg-blue-50/30 transition-colors`}
                            >
                              {row.getVisibleCells().map(cell => (
                                <td className="px-4 py-3" key={cell.id}>
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext(),
                                  )}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                      <p className="text-xs text-gray-600">
                        Total:{' '}
                        <span className="font-semibold">{mailLogs.length}</span>{' '}
                        email{mailLogs.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            }
          />
        )}
      </AnimatePresence>

      {/* Remarks Modal */}
      <Modal
        title={
          <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
            <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg shadow-md">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">
                {editingRemark ? 'Update Remark' : 'Customer Remarks'}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {editingRemark
                  ? 'Edit existing remark'
                  : 'Add notes and observations'}
              </p>
            </div>
          </div>
        }
        open={isRemarksModalOpen}
        onCancel={() => {
          setIsRemarksModalOpen(false);
          setEditingRemark(null);
          remarkForm.reset();
        }}
        footer={null}
        width={720}
        className="remarks-modal"
      >
        <div className="py-4">
          <form onSubmit={remarkForm.handleSubmit(handleRemarkSubmit)}>
            <div className="mb-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg p-4 border border-emerald-200">
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                {editingRemark ? 'Edit Your Remark' : 'Add New Remark'}
              </label>
              <Controller
                name="remarks"
                control={remarkForm.control}
                rules={{ required: 'Remarks are required' }}
                render={({ field, fieldState }) => (
                  <>
                    <Input.TextArea
                      {...field}
                      rows={3}
                      placeholder="Type your remark here..."
                      className="w-full"
                      style={{
                        borderRadius: '8px',
                        borderColor: fieldState.error ? '#ef4444' : '#d1d5db',
                      }}
                    />
                    {fieldState.error && (
                      <div className="flex items-center gap-1 text-red-600 text-xs mt-2">
                        <X className="w-3 h-3" />
                        {fieldState.error.message}
                      </div>
                    )}
                  </>
                )}
              />
              <div className="flex justify-end gap-2 mt-3">
                <Button
                  onClick={() => {
                    setEditingRemark(null);
                    remarkForm.reset({ remarks: '' });
                  }}
                  size="small"
                  disabled={!editingRemark}
                >
                  Cancel Edit
                </Button>
                <Button
                  htmlType="submit"
                  type="primary"
                  size="small"
                  loading={isCreatingRemark || isUpdatingRemark}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {editingRemark ? 'Update' : 'Add'} Remark
                </Button>
              </div>
            </div>

            {/* Existing Remarks */}
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wide">
                Previous Remarks ({remarks.length})
              </h4>
              {remarks.length > 0 && (
                <Select
                  value={remarksSortBy}
                  onChange={setRemarksSortBy}
                  style={{ width: 160 }}
                  size="small"
                  placeholder="Sort"
                  suffixIcon={
                    <SwapOutlined
                      rotate={90}
                      style={{ color: '#6B7280', fontSize: 11 }}
                    />
                  }
                  options={[
                    {
                      label: 'Recent First',
                      value: PaymentReminderRemarksSortBy.CREATED_DATE_DESC,
                    },
                    {
                      label: 'Oldest First',
                      value: PaymentReminderRemarksSortBy.CREATED_DATE_ASC,
                    },
                  ]}
                />
              )}
            </div>

            {isLoadingRemarks ? (
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <Skeleton active paragraph={{ rows: 2 }} />
              </div>
            ) : remarks.length > 0 ? (
              <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
                {remarks.map((remark, index) => (
                  <div
                    key={remark.id}
                    className="group relative bg-white rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow-md hover:border-emerald-300 transition-all"
                  >
                    <div className="absolute top-3 right-3 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleEditRemark(remark)}
                        disabled={isUpdatingRemark}
                        className="px-2 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded transition-colors disabled:opacity-50"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteRemark(remark.id)}
                        disabled={isDeletingRemark}
                        className="px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                      >
                        Delete
                      </button>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-lg flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-emerald-700">
                          {index + 1}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0 pr-20">
                        <p className="text-sm text-gray-800 leading-relaxed break-words">
                          {remark.remarks}
                        </p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                            {formatDayJsDate(
                              dayjs(remark.createdAt),
                              'Do MMM, YY',
                            )}
                          </span>
                          {remark.createdByUser && (
                            <span className="flex items-center gap-1">
                              <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                              {remark.createdByUser.FullName || 'Unknown'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg border-2 border-dashed border-gray-300">
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-3 shadow-sm">
                  <MessageSquare className="w-7 h-7 text-gray-400" />
                </div>
                <h4 className="text-sm font-semibold text-gray-800 mb-1">
                  No Remarks Yet
                </h4>
                <p className="text-xs text-gray-500 text-center max-w-xs">
                  Be the first to add a remark for this customer
                </p>
              </div>
            )}
          </form>
        </div>
      </Modal>

      {/* Delete Remark Confirmation */}
      <ConfirmationDialog
        isOpen={remarkToDelete !== null}
        title="Delete Remark"
        onConfirm={handleDeleteRemarkConfirm}
        onCancel={() => setRemarkToDelete(null)}
        loading={isDeletingRemark}
      >
        <p className="text-gray-700 text-sm">
          Are you sure you want to delete this remark? This action cannot be
          undone.
        </p>
      </ConfirmationDialog>

      {/* Update File Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
              <FileUp className="w-4 h-4 text-indigo-600" />
            </div>
            <span>Re-upload Payment Reminder File</span>
          </div>
        }
        open={isUpdateModalOpen}
        onCancel={() => {
          setIsUpdateModalOpen(false);
          setUpdateFileList([]);
        }}
        footer={null}
        width={550}
      >
        <div className="py-4">
          <Upload {...updateUploadProps} maxCount={1}>
            <Button
              icon={<UploadOutlined />}
              className="w-full"
              type="dashed"
              size="large"
            >
              Click to Select File
            </Button>
          </Upload>
          <div className="mt-2 text-xs text-gray-500">
            Supported formats: Excel (.xlsx, .xls) or CSV
          </div>
        </div>
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <Button
            onClick={handleDownloadTemplate}
            disabled={isDownloadingTemplate}
            icon={<DownloadIcon className="w-4 h-4" />}
            className="text-xs"
          >
            Download Template
          </Button>
          <div className="flex gap-2">
            <Button
              onClick={() => {
                setIsUpdateModalOpen(false);
                setUpdateFileList([]);
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleUpdateFile}
              loading={isUpdatingFile}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              Re-upload
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bucket Invoice Details Modal */}
      <BucketInvoiceDetailsModal
        open={!!bucketInvoiceDetails}
        onClose={() => {
          setIsBucketInvoiceModalOpen(false);
          setBucketInvoiceDetails(null);
          setBucketInvoicePage(1);
          setBucketInvoicePageSize(25);
          setLastBucketInvoiceRequest('');
          setShouldClearData(false);
        }}
        onAfterOpenChange={open => {
          setIsBucketInvoiceModalOpen(open);
          if (!open) {
            setLastBucketInvoiceRequest('');
            setShouldClearData(false);
          }
        }}
        isLoading={shouldShowLoader}
        error={bucketInvoiceError}
        data={shouldShowData ? bucketInvoiceData : undefined}
        currentPage={bucketInvoicePage}
        currentPageSize={bucketInvoicePageSize}
        onPageChange={(page: number, pageSize: number) => {
          setBucketInvoicePage(page);
          setBucketInvoicePageSize(pageSize);
        }}
      />
    </div>
  );
}
